import { useState, useRef, useEffect } from 'react';
import { useChatMessage } from '@/hooks/useChat';
import type { ChatMessage } from '@/lib/types';
import { cn } from '@/lib/cn';

interface ChatInterfaceProps {
  conversationId: string;
  chatHistory: ChatMessage[];
  onDraftUpdate?: (draft: any) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export default function ChatInterface({
  conversationId,
  chatHistory,
  onDraftUpdate,
  isCollapsed = false,
  onToggleCollapse,
}: ChatInterfaceProps) {
  const [message, setMessage] = useState('');
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatMutation = useChatMessage();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!isCollapsed && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [chatHistory, isCollapsed]);

  const handleSend = () => {
    if (!message.trim() || chatMutation.isPending) return;

    chatMutation.mutate(
      { conversationId, message: message.trim() },
      {
        onSuccess: (data) => {
          // If the chat response includes an updated draft, notify parent
          if (data.updated_draft && onDraftUpdate) {
            onDraftUpdate(data.updated_draft);
          }
          setMessage('');
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl + Enter to send
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
    // Prevent Enter from submitting any parent form
    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
    }
  };

  return (
    <div className="rounded-lg border">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-sm">Chat with AI</h3>
          {chatHistory.length > 0 && (
            <span className="text-xs text-muted-foreground">
              ({chatHistory.length} message{chatHistory.length !== 1 ? 's' : ''})
            </span>
          )}
        </div>
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            {isCollapsed ? '▼' : '▲'}
          </button>
        )}
      </div>

      {/* Chat Messages */}
      {!isCollapsed && (
        <>
          <div ref={messagesContainerRef} className="max-h-[300px] overflow-y-auto p-3 space-y-3">
            {chatHistory.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">
                  Ask the AI questions about this conversation
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  e.g., "Should I offer a refund?" or "What's the customer's main issue?"
                </p>
              </div>
            ) : (
              <>
                {chatHistory.map((msg, index) => (
                  <ChatMessageBubble key={index} message={msg} />
                ))}
                {chatMutation.isPending && (
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-xs text-primary-foreground">AI</span>
                    </div>
                    <div className="flex-1 bg-muted rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        <span className="text-sm text-muted-foreground">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Input Area */}
          <div className="border-t p-3">
            <div className="flex gap-2">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask a question... (Cmd+Enter to send)"
                className="flex-1 px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                rows={2}
                disabled={chatMutation.isPending}
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!message.trim() || chatMutation.isPending}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed self-end"
              >
                Send
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Press Cmd+Enter to send
            </p>
          </div>

          {/* Error Message */}
          {chatMutation.isError && (
            <div className="px-3 pb-3">
              <div className="rounded-lg border border-red-200 bg-red-50 p-2">
                <p className="text-xs text-red-800">
                  Failed to send message. Please try again.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ChatMessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex items-start gap-2', isUser && 'flex-row-reverse')}>
      <div
        className={cn(
          'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs',
          isUser ? 'bg-accent' : 'bg-primary text-primary-foreground'
        )}
      >
        {isUser ? 'U' : 'AI'}
      </div>
      <div
        className={cn(
          'flex-1 rounded-lg p-3 text-sm whitespace-pre-wrap',
          isUser ? 'bg-accent' : 'bg-muted'
        )}
      >
        {message.content}
      </div>
    </div>
  );
}
